import { ListIcon } from '@phosphor-icons/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';

import {
  Box,
  ButtonIcon,
  Hidden,
  IconProfile,
  Link,
  Text,
} from 'braid-design-system';
import {
  createRef,
  type Dispatch,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { Logo } from '#src/components/Logo/Logo.tsx';
import { SiteSearch } from '#src/components/SiteSearch/SiteSearch.tsx';

import { NavContext, type TabLinePosition } from '#src/hooks/nav.tsx';
import * as styles from './Nav.css';
import { ProfileMenu } from './ProfileMenu/ProfileMenu.tsx';
import { useClickOutside } from '#src/hooks/useClickOutside.tsx';

const NavLink = forwardRef<
  HTMLElement,
  {
    active: boolean;
    activeTabPosition?: TabLinePosition | null;
    children: ReactNode;
    href: string;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  }
>(({ active, activeTabPosition, children, href, onClick }, ref) => (
  <Link href={href} onClick={onClick}>
    <Box ref={ref} position="relative" paddingBottom="small">
      <Text>
        <Box
          component="span"
          className={{
            [styles.navLinkInner]: true,
            [styles.navLinkActive]: active,
          }}
        >
          <Box component="span" position="relative">
            {children}
          </Box>
        </Box>
      </Text>

      {activeTabPosition || !active ? null : (
        <Box
          component="span"
          position="absolute"
          display="block"
          left={0}
          right={0}
          bottom={0}
          pointerEvents="none"
          className={styles.tabUnderlinePreload}
        />
      )}
    </Box>
  </Link>
));

export const Nav = ({
  setMenuOpened,
}: {
  setMenuOpened: Dispatch<SetStateAction<boolean>>;
}) => {
  const location = useLocation();

  const [activeTabPosition, setActiveTabPosition] = useContext(NavContext);

  const tabRefs = {
    docs: useRef<HTMLElement>(null),
    dashboard: useRef<HTMLElement>(null),
    schema: useRef<HTMLElement>(null),
  };

  const active = {
    docs:
      !location.pathname.startsWith('/manage') &&
      !location.pathname.startsWith('/schema') &&
      location.pathname !== '/authorize',
    // TODO: Fix this so that the /authorize page is not considered active or we have a way to determine the page being redirected to
    dashboard:
      location.pathname.startsWith('/manage') ||
      location.pathname === '/authorize',
    schema: location.pathname.startsWith('/schema'),
  };

  const selectedTabKey = (
    Object.keys(active) as Array<keyof typeof active>
  ).find((key) => active[key]);

  const selectedTab = selectedTabKey ? tabRefs[selectedTabKey] : null;

  useEffect(() => {
    const element = selectedTab?.current;

    const recalculate = () => {
      if (selectedTab?.current) {
        const position = {
          left: selectedTab.current.offsetLeft,
          width: selectedTab.current.offsetWidth,
        };

        if (
          activeTabPosition?.left !== position.left ||
          activeTabPosition?.width !== position.width
        ) {
          setActiveTabPosition(position);
        }
      }
    };

    recalculate();

    const observer = new ResizeObserver(recalculate);

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [selectedTab, activeTabPosition, setActiveTabPosition]);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = createRef<HTMLDivElement>();
  useClickOutside(profileMenuRef, () => {
    setShowProfileMenu(false);
  });

  return (
    <Box
      className={styles.nav}
      component="nav"
      paddingX={['xxsmall', 'small']}
      display="flex"
      alignItems="center"
      justifyContent="spaceBetween"
      position="relative"
      gap="xxxlarge"
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent={['center', 'spaceBetween']}
        height="full"
        paddingTop={['none', 'small']}
      >
        <Link href="/">
          <Logo />
        </Link>

        <Hidden below="tablet">
          <Box display="flex" gap="small" position="relative">
            <NavLink
              href="/"
              active={active.docs}
              activeTabPosition={activeTabPosition}
              ref={tabRefs.docs}
            >
              Docs
            </NavLink>

            <NavLink
              href="/manage"
              active={active.dashboard}
              activeTabPosition={activeTabPosition}
              ref={tabRefs.dashboard}
            >
              Dashboard
            </NavLink>

            <NavLink
              href="/schema"
              active={active.schema}
              activeTabPosition={activeTabPosition}
              ref={tabRefs.schema}
            >
              Schema
            </NavLink>

            {activeTabPosition ? (
              <Box
                component="span"
                position="absolute"
                display="block"
                left={0}
                right={0}
                bottom={0}
                pointerEvents="none"
                className={[styles.tabUnderline]}
                style={assignInlineVars({
                  [styles.underlineLeft]: activeTabPosition.left.toString(),
                  [styles.underlineWidth]: activeTabPosition.width.toString(),
                })}
              />
            ) : null}
          </Box>
        </Hidden>
      </Box>

      {/* Hidden doesn't allow us to set flex-grow etc */}
      <Box className={styles.wideOnlySearchSection}>
        <Box
          display="flex"
          width="full"
          gap="small"
          paddingBottom="small"
          alignItems="center"
        >
          <SiteSearch />

          <Box
            id="profile-dropdown-menu"
            position="relative"
            display="inlineBlock"
          >
            <ButtonIcon
              id="profile-menu-button-icon"
              icon={<IconProfile />}
              label="Profile"
              onClick={() => setShowProfileMenu((show) => !show)}
            />
            {showProfileMenu ? (
              <Box
                ref={profileMenuRef}
                id="profile-dropdown-menu-content"
                className={styles.profileMenuContainer}
                paddingTop="small"
              >
                <ProfileMenu />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Hidden above="desktop">
        <Box
          aria-label="Menu"
          component="button"
          className={styles.menuIcon}
          onClick={() => setMenuOpened((open) => !open)}
        >
          <ListIcon size="100%" />
        </Box>
      </Hidden>
    </Box>
  );
};
